import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Loader2, X } from 'lucide-react';

import type {
  ForecastReviewBulkActionResponse,
  ForecastReviewBulkApprovePreview,
} from '@/services/forecastReviewService';
import type { ApproveAlertStep } from '../hooks/useApproveAlertProgressSteps';
import { ApproveAlertProgressSteps } from './ApproveAlertProgressSteps';
import { formatUiNumber } from '@/utils/uiLocale';

export type BulkApproveModalPhase = 'confirm' | 'applying' | 'done' | 'error';

interface BulkApproveModalProps {
  open: boolean;
  preview: ForecastReviewBulkApprovePreview | null;
  previewLoading: boolean;
  phase: BulkApproveModalPhase;
  applySteps: ApproveAlertStep[];
  result: ForecastReviewBulkActionResponse | null;
  errorMessage: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

function formatQty(value: number, language?: string | null): string {
  return formatUiNumber(value, { maximumFractionDigits: 1 }, language);
}

export function BulkApproveModal({
  open,
  preview,
  previewLoading,
  phase,
  applySteps,
  result,
  errorMessage,
  onConfirm,
  onClose,
}: BulkApproveModalProps) {
  const { t, i18n } = useTranslation();

  if (!open) return null;

  const isBusy = phase === 'applying' || previewLoading;
  const canClose = !isBusy;
  const alertCount = preview?.alert_count ?? 0;

  const content = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
        onClick={canClose ? onClose : undefined}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-approve-modal-title"
        className="relative w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 id="bulk-approve-modal-title" className="text-base font-semibold text-gray-900">
              {phase === 'done'
                ? t('forecastReview.alerts.bulkApproveModal.doneTitle')
                : t('forecastReview.alerts.bulkApproveModal.title')}
            </h2>
            {alertCount > 0 ? (
              <p className="mt-1 text-sm text-gray-600">
                {t('forecastReview.alerts.bulkApproveModal.scope', { count: alertCount })}
              </p>
            ) : null}
          </div>
          {canClose ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label={t('forecastReview.alerts.bulkApproveModal.close')}
            >
              <X className="h-5 w-5" />
            </button>
          ) : null}
        </div>

        <div className="px-5 py-4">
          {phase === 'applying' ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                {t('forecastReview.alerts.bulkApproveModal.progressHint')}
              </p>
              <ApproveAlertProgressSteps steps={applySteps} />
            </div>
          ) : phase === 'done' && result ? (
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {t('forecastReview.alerts.bulkApproveModal.success', {
                    succeeded: result.succeeded_count,
                    requested: result.requested_count,
                  })}
                </span>
              </div>
              {result.failed_count > 0 ? (
                <p className="text-amber-800">
                  {t('forecastReview.alerts.bulkApproveModal.partialFailures', {
                    failed: result.failed_count,
                  })}
                </p>
              ) : null}
            </div>
          ) : phase === 'error' ? (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{errorMessage ?? t('forecastReview.alerts.bulkApproveModal.errorGeneric')}</span>
            </div>
          ) : previewLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('forecastReview.alerts.bulkApproveModal.loadingPreview')}
            </div>
          ) : preview && preview.alert_count > 0 ? (
            <>
              <p className="text-sm text-gray-600">
                {t('forecastReview.alerts.bulkApproveModal.message')}
              </p>
              <dl className="mt-4 space-y-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-3 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">{t('forecastReview.alerts.bulkApproveModal.products')}</dt>
                  <dd className="font-medium text-gray-900">{preview.product_count}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">{t('forecastReview.alerts.bulkApproveModal.currentTotal')}</dt>
                  <dd>{formatQty(preview.current_forecast_total_quantity, i18n.language)}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">{t('forecastReview.alerts.bulkApproveModal.projectedTotal')}</dt>
                  <dd className="font-medium text-gray-900">
                    {formatQty(preview.projected_forecast_total_quantity, i18n.language)}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500">{t('forecastReview.alerts.bulkApproveModal.delta')}</dt>
                  <dd
                    className={
                      preview.delta_quantity > 0
                        ? 'font-medium text-amber-700'
                        : preview.delta_quantity < 0
                          ? 'font-medium text-emerald-700'
                          : 'text-gray-700'
                    }
                  >
                    {preview.delta_quantity > 0 ? '+' : ''}
                    {formatQty(preview.delta_quantity, i18n.language)}
                  </dd>
                </div>
              </dl>
            </>
          ) : (
            <p className="text-sm text-gray-600">{t('forecastReview.alerts.bulkApproveModal.empty')}</p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
          {phase === 'confirm' ? (
            <>
              <button
                type="button"
                className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                onClick={onClose}
                disabled={isBusy}
              >
                {t('forecastReview.alerts.bulkApproveModal.cancel')}
              </button>
              <button
                type="button"
                className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                onClick={() => void onConfirm()}
                disabled={isBusy || !preview || preview.alert_count === 0}
              >
                {t('forecastReview.alerts.bulkApproveModal.confirm')}
              </button>
            </>
          ) : phase === 'done' || phase === 'error' ? (
            <button
              type="button"
              className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
              onClick={onClose}
            >
              {t('forecastReview.alerts.bulkApproveModal.close')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
